import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import * as fromShipmentAttachments from './shipment-attachments.reducer';
import * as ShipmentAttachmentsActions from './shipment-attachments.actions';
import * as ShipmentAttachmentsSelectors from './shipment-attachments.selectors';

import { ShipmentAttachmentsServiceBase } from '../shipment-attachments-service-base';

import {
    catchError,
    map,
    mergeMap,
    switchMap,
    take,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import { of } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { Store } from '@ngrx/store';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class ShipmentAttachmentsEffects {
    private isExternalLink(link: string): boolean {
        return link.includes('appgw.magaya.net');
    }

    private getMimeType(extension: string): string {
        const mimeTypes: Record<string, string> = {
            pdf: 'application/pdf',
            png: 'image/png',
            jpg: 'image/jpeg',
            jpeg: 'image/jpeg',
            gif: 'image/gif',
            webp: 'image/webp',
            txt: 'text/plain',
            html: 'text/html',
            mp4: 'video/mp4',
            mp3: 'audio/mpeg',
        };
        return (
            mimeTypes[extension?.toLowerCase()] ?? 'application/octet-stream'
        );
    }

    init$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShipmentAttachmentsActions.init),
            switchMap(action =>
                this.service.getShipmentAttachments(action.shipment.id).pipe(
                    map(shipmentAttachments =>
                        ShipmentAttachmentsActions.loadShipmentAttachmentsSuccess({
                            shipmentAttachments,
                        })
                    ),
                    catchError(error => {
                        console.error(error);
                        return of(
                            ShipmentAttachmentsActions.loadShipmentAttachmentsFailure({
                                error,
                            })
                        );
                    })
                )
            )
        )
    );

    loadAttachmentsOnly$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShipmentAttachmentsActions.loadShipmentAttachmentsOnly),
            withLatestFrom(
                this.store.select(ShipmentAttachmentsSelectors.getAllShipmentAttachments)
            ),
            switchMap(([action]) =>
                this.service.getShipmentAttachments(action.shipment.id).pipe(
                    take(1),
                    map(response =>
                        ShipmentAttachmentsActions.loadShipmentAttachmentsOnlySuccess({
                            attachmentLinks: response,
                        })
                    ),
                    catchError(error =>
                        of(
                            ShipmentAttachmentsActions.loadShipmentAttachmentsOnlyFailure({
                                error,
                            })
                        )
                    )
                )
            )
        )
    );

    generateAttachmentLink$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShipmentAttachmentsActions.generateAttachmentLink),
            tap(() =>
                this.toastr.info(
                    this.translate.instant('common.your-item-is-being-loaded', {
                        value: this.translate
                            .instant('common.attachment')
                            .toLowerCase(),
                    })
                )
            ),
            switchMap(action =>
                this.service
                    .generateShipmentAttachmentLink(
                        action.shipment.id,
                        action.attachment.id,
                        action.attachment.parent_type
                    )
                    .pipe(
                        map(attachmentLink =>
                            ShipmentAttachmentsActions.generateAttachmentLinkSuccess({
                                attachment: action.attachment,
                                attachmentLink,
                                action: action.action,
                            })
                        ),
                        catchError(error => {
                            console.error(error);
                            return of(
                                ShipmentAttachmentsActions.generateAttachmentLinkFailure({
                                    error,
                                })
                            );
                        })
                    )
            )
        )
    );

    generateAttachmentLinkSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShipmentAttachmentsActions.generateAttachmentLinkSuccess),
            mergeMap(action => {
                const isExternal = this.isExternalLink(action.attachmentLink);
                if (isExternal) {
                    return this.http
                        .get(action.attachmentLink, { responseType: 'blob' })
                        .pipe(
                            mergeMap(blob => {
                                const filename = action.attachment.filename;
                                const extension = filename.split('.').pop() || '';
                                const mimeType = this.getMimeType(extension);
                                const typedBlob = new Blob([blob], {
                                    type: mimeType,
                                });

                                return of(
                                    ShipmentAttachmentsActions.downloadFileBlob({
                                        blob: typedBlob,
                                        filename,
                                        mimeType,
                                    })
                                );
                            }),
                            catchError(error => {
                                console.error('Error processing attachment link:', error);
                                return of(
                                    ShipmentAttachmentsActions.generateAttachmentLinkFailure({
                                        error,
                                    })
                                );
                            })
                        );
                } else {
                    const newWindow = window.open(action.attachmentLink, '_blank');
                    if (!newWindow) {
                        this.toastr.error(
                            this.translate.instant('common.popup-blocked-message')
                        );
                    }
                }
            })
        )
    );

    downloadFileBlob$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ShipmentAttachmentsActions.downloadFileBlob),
                tap(action => {
                    const url = window.URL.createObjectURL(action.blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = action.filename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                })
            ),
        { dispatch: false }
    );

    genericFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    ShipmentAttachmentsActions.loadShipmentAttachmentsFailure,
                    ShipmentAttachmentsActions.loadShipmentAttachmentsOnlyFailure,
                    ShipmentAttachmentsActions.generateAttachmentLinkFailure,
                    ShipmentAttachmentsActions.createShipmentAttachmentFailure,
                    ShipmentAttachmentsActions.deleteShipmentAttachmentFailure
                ),
                tap(action => {
                    this.toastr.error(
                        action.error?.error?.message ??
                            this.translate.get('common.generic-error-alt-3'),
                        this.translate.instant(
                            'portal.booking.create-booking.something-went-wrong'
                        )
                    );
                })
            ),
        { dispatch: false }
    );

    createShipmentAttachment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShipmentAttachmentsActions.createShipmentAttachment),
            switchMap(action =>
                this.service
                    .createShipmentAttachment(action.shipment.id, action.request)
                    .pipe(
                        map(shipmentAttachment =>
                            ShipmentAttachmentsActions.createShipmentAttachmentSuccess({
                                shipmentAttachment,
                            })
                        ),
                        catchError(error => {
                            console.error(error);
                            return of(
                                ShipmentAttachmentsActions.createShipmentAttachmentFailure({
                                    error,
                                })
                            );
                        })
                    )
            )
        )
    );

    deleteShipmentAttachment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShipmentAttachmentsActions.deleteShipmentAttachment),
            switchMap(action =>
                this.service
                    .deleteShipmentAttachment(action.shipmentId, action.shipmentAttachmentId)
                    .pipe(
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        map(_ =>
                            ShipmentAttachmentsActions.deleteShipmentAttachmentSuccess({
                                shipmentAttachmentId: action.shipmentAttachmentId,
                            })
                        ),
                        catchError(error =>
                            of(
                                ShipmentAttachmentsActions.deleteShipmentAttachmentFailure({
                                    error,
                                })
                            )
                        )
                    )
            )
        )
    );

    deleteShipmentAttachmentSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ShipmentAttachmentsActions.deleteShipmentAttachmentSuccess),
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                tap(_ => {
                    this.toastr.success(
                        this.translate.instant(
                            'portal.attachments.attachment-deleted'
                        )
                    );
                })
            ),
        { dispatch: false }
    );

    constructor(
        private readonly actions$: Actions,
        private readonly http: HttpClient,
        private readonly toastr: ToastrService,
        private readonly translate: TranslateService,
        private readonly service: ShipmentAttachmentsServiceBase,
        private readonly store: Store<fromShipmentAttachments.ShipmentAttachmentsPartialState>
    ) {}
}
